import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, Row, Col, Table, Badge, Spinner, Tabs, Tab, Form, Button } from 'react-bootstrap';
import { FaUserMd, FaCalendarCheck, FaFileInvoiceDollar, FaMoneyBillWave, FaChartLine, FaHistory, FaArrowLeft } from 'react-icons/fa';

const DoctorPortal = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [stats, setStats] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [allBills, setAllBills] = useState([]);
  const [allPayments, setAllPayments] = useState([]);

  useEffect(() => {
    fetchDoctorData();
  }, [id]);

  useEffect(() => {
    filterData();
  }, [startDate, endDate, allAppointments, allBills, allPayments]);

  const filterData = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    setAppointments(allAppointments.filter(apt => {
      const aptDate = new Date(apt.appointmentDate);
      return aptDate >= start && aptDate <= end;
    }));

    setBills(allBills.filter(bill => {
      const billDate = new Date(bill.createdAt);
      return billDate >= start && billDate <= end;
    }));

    setPayments(allPayments.filter(payment => {
      const payDate = new Date(payment.createdAt);
      return payDate >= start && payDate <= end;
    }));
  };

  const fetchDoctorData = async () => {
    try {
      const [doctorRes, statsRes, appointmentsRes, billsRes, paymentsRes, salariesRes] = await Promise.all([
        axios.get(`/api/doctors.php?id=${id}`),
        axios.get(`/api/doctors.php?id=${id}&action=stats`),
        axios.get(`/api/appointments.php?doctorId=${id}`),
        axios.get(`/api/billings.php?doctorId=${id}`),
        axios.get(`/api/doctor-payments.php?doctorId=${id}`),
        axios.get(`/api/doctor-salaries.php?doctorId=${id}`)
      ]);
      
      setDoctor(doctorRes.data);
      setStats(statsRes.data);
      setAllAppointments(appointmentsRes.data);
      setAllBills(billsRes.data);
      setAllPayments(paymentsRes.data);
      setSalaries(salariesRes.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-PK', { style: 'currency', currency: 'PKR' });
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/doctors')}>
          <FaArrowLeft className="me-1" /> Back
        </Button>
      </div>
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={8}>
              <h2><FaUserMd className="me-2" />Dr. {doctor?.name}</h2>
              <p className="text-muted mb-0">{doctor?.specialization} | {doctor?.qualification}</p>
              <p className="text-muted">{doctor?.contactNumber} | {doctor?.email}</p>
            </Col>
            <Col md={4} className="text-end">
              <Badge bg={doctor?.isActive ? 'success' : 'secondary'} className="px-3 py-2">
                {doctor?.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <Row className="align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Start Date</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">End Date</Form.Label>
                <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Button variant="primary" className="w-100" onClick={filterData}>
                <FaChartLine className="me-2" /> Filter Data
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #007bff'}}>
            <Card.Body>
              <p className="text-muted mb-1 small">TOTAL APPOINTMENTS</p>
              <h3 className="mb-0 text-primary fw-bold">{appointments.length}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #17a2b8'}}>
            <Card.Body>
              <p className="text-muted mb-1 small">TOTAL BILLS</p>
              <h3 className="mb-0 text-info fw-bold">{bills.length}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #28a745'}}>
            <Card.Body>
              <p className="text-muted mb-1 small">TOTAL BILL AMOUNT</p>
              <h3 className="mb-0 text-success fw-bold">{formatCurrency(bills.reduce((sum, bill) => sum + parseFloat(bill.totalAmount || 0), 0))}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #ffc107'}}>
            <Card.Body>
              <p className="text-muted mb-1 small">BILL PAYMENTS RECEIVED</p>
              <h3 className="mb-0 text-warning fw-bold">{formatCurrency(bills.reduce((sum, bill) => sum + parseFloat(bill.paidAmount || 0), 0))}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Tabs defaultActiveKey="appointments" className="mb-3">
            <Tab eventKey="appointments" title={<span><FaCalendarCheck className="me-2" />Appointments</span>}>
              <Table responsive hover>
                <thead className="bg-light">
                  <tr>
                    <th>Date</th>
                    <th>Patient</th>
                    <th>Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((apt) => (
                    <tr key={apt.id}>
                      <td>{new Date(apt.appointmentDate).toLocaleDateString('en-GB')}</td>
                      <td>{apt.Patient?.name}</td>
                      <td>{apt.appointmentTime}</td>
                      <td>
                        <Badge bg={apt.status === 'completed' ? 'success' : apt.status === 'cancelled' ? 'danger' : 'warning'}>
                          {apt.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Tab>

            <Tab eventKey="bills" title={<span><FaFileInvoiceDollar className="me-2" />Bills</span>}>
              <Table responsive hover>
                <thead className="bg-light">
                  <tr>
                    <th>Date</th>
                    <th>Bill #</th>
                    <th>Patient</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((bill) => (
                    <tr key={bill.id}>
                      <td>{new Date(bill.createdAt).toLocaleDateString('en-GB')}</td>
                      <td>{bill.billNumber}</td>
                      <td>{bill.Patient?.name}</td>
                      <td>{formatCurrency(bill.totalAmount)}</td>
                      <td>
                        <Badge bg={bill.paymentStatus === 'complete' ? 'success' : bill.paymentStatus === 'partial' ? 'warning' : 'danger'}>
                          {bill.paymentStatus}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Tab>

            <Tab eventKey="salaries" title={<span><FaChartLine className="me-2" />Salaries</span>}>
              <div className="mb-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Salary History</h5>
                <Button variant="primary" size="sm" onClick={() => window.open(`/doctor-payment-history/${id}`, '_blank')}>
                  <FaHistory className="me-2" /> View Full History
                </Button>
              </div>
              <Table responsive hover>
                <thead className="bg-light">
                  <tr>
                    <th>Month</th>
                    <th>Amount</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {salaries.map((salary) => (
                    <tr key={salary.id}>
                      <td>{new Date(salary.month + '-01').toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}</td>
                      <td>{formatCurrency(salary.amount)}</td>
                      <td>{salary.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DoctorPortal;
